import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { PRINT_SIZES, PrintSize } from '../../types/cart';
import { useCart } from '../../contexts/CartContext';
import Button from './Button';

interface AddToCartModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageId: string;
  imageSrc: string;
  imageTitle: string;
  imageDescription?: string;
}

const AddToCartModal: React.FC<AddToCartModalProps> = ({
  isOpen,
  onClose,
  imageId,
  imageSrc,
  imageTitle,
  imageDescription
}) => {
  const { t } = useTranslation();
  const { addToCart } = useCart();
  const [selectedSize, setSelectedSize] = useState<PrintSize>(
    PRINT_SIZES.find(s => s.recommended) || PRINT_SIZES[3] // Default to recommended (40×60cm Canvas)
  ); 
  const [quantity, setQuantity] = useState(1);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showCheckoutSuggestion, setShowCheckoutSuggestion] = useState(false);

  const handleAddToCart = () => {
    addToCart(imageId, imageSrc, imageTitle, selectedSize, quantity);
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
      setShowCheckoutSuggestion(true);
    }, 1000);
  };

  const handleContinueShopping = () => {
    setShowCheckoutSuggestion(false);
    onClose();
    // Reset for next time
    setQuantity(1);
    setSelectedSize(PRINT_SIZES.find(s => s.recommended) || PRINT_SIZES[3]);
  };

  const handleGoToCheckout = () => {
    setShowCheckoutSuggestion(false);
    onClose();
    // TODO: Navigate to cart/checkout page
    window.location.hash = '#cart';
  };

  const totalPrice = selectedSize.price * quantity;

  const handleQuantityChange = (delta: number) => {
    const newQuantity = Math.max(1, Math.min(10, quantity + delta));
    setQuantity(newQuantity);
  };

  if (!isOpen) return null;

  // Show checkout suggestion after adding to cart
  if (showCheckoutSuggestion) {
    return (
      <AnimatePresence>
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={handleContinueShopping}
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="relative bg-dark-surface border border-dark-border/30 rounded-2xl shadow-2xl max-w-lg w-full p-6 sm:p-8 text-center mx-4 sm:mx-0"
          >
            {/* Success Icon */}
            <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>

            <h3 className="font-playfair text-2xl text-cream mb-2">{t('cart.addedToCart')}</h3>
            <p className="text-cream-dark mb-6">{t('cart.whatNext')}</p>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={handleContinueShopping}
                variant="outline"
                size="lg"
                className="flex-1"
              >
                {t('cart.continueShopping')}
              </Button>
              <Button
                onClick={handleGoToCheckout}
                variant="primary"
                size="lg"
                className="flex-1 shadow-2xl"
              >
                <span className="flex items-center justify-center">
                  <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  {t('cart.viewCart')}
                </span>
              </Button>
            </div>
          </motion.div>
        </div>
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/80 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Modal Content */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="relative bg-dark-surface border border-dark-border/30 rounded-2xl shadow-2xl max-w-5xl w-full max-h-[95vh] overflow-auto mx-4 sm:mx-0"
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 p-2 rounded-full bg-dark-primary/50 hover:bg-dark-primary/70 transition-colors"
          >
            <svg className="w-6 h-6 text-cream" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div className="grid md:grid-cols-2 gap-4 sm:gap-6 p-4 sm:p-6">
            {/* Image Preview */}
            <div>
              <div className="aspect-[3/4] rounded-lg overflow-hidden bg-dark-primary">
                <img
                  src={imageSrc}
                  alt={imageTitle}
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="mt-4 font-playfair text-xl text-cream">{imageTitle}</h3>
              {imageDescription && (
                <p className="mt-2 text-cream-dark text-sm">{imageDescription}</p>
              )}
            </div>

            {/* Options */}
            <div className="space-y-4">
              <div>
                <h2 className="font-playfair text-2xl text-cream mb-2">{t('cart.customizeOrder')}</h2>
                <div className="w-16 h-px bg-gold/50"></div>
              </div>

              {/* Price Display and Add to Cart Button - Moved to Top */}
              <div className="p-4 bg-dark-primary/30 rounded-lg border border-gold/20">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-cream-dark">{t('cart.totalPrice')}</span>
                  <span className="text-3xl font-light text-gold">€{totalPrice}</span>
                </div>

                {/* Add to Cart Button */}
                <Button
                  onClick={handleAddToCart}
                  variant="primary"
                  size="lg"
                  className="w-full mb-2 shadow-2xl"
                  disabled={showSuccess}
                >
                  {showSuccess ? (
                    <span className="flex items-center justify-center">
                      <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {t('cart.addedToCart')}
                    </span>
                  ) : (
                    <span className="flex items-center justify-center">
                      <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      {t('cart.addToCart')}
                    </span>
                  )}
                </Button>

                {/* Continue Shopping */}
                <button
                  onClick={onClose}
                  className="w-full text-cream-dark hover:text-cream text-sm font-light transition-colors"
                >
                  {t('cart.continueShopping')}
                </button>
              </div>

              {/* Size Selection by Print Type */}
              <div>
                <label className="block text-cream mb-3 font-light">{t('cart.selectSize')}</label>
                
                {/* Canvas Prints */}
                <div className="mb-4">
                  <h4 className="text-sm text-gold mb-2">{t('cart.canvasPrints')}</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {PRINT_SIZES.filter(size => size.printType === 'canvas').map((size) => (
                      <button
                        key={size.id}
                        onClick={() => setSelectedSize(size)}
                        className={`p-2 rounded-lg border text-left transition-all relative ${
                          selectedSize.id === size.id
                            ? 'border-gold bg-gold/10 text-gold'
                            : 'border-dark-border/30 hover:border-gold/50 text-cream-dark hover:text-cream'
                        }`}
                      >
                        {size.recommended && (
                          <div className="absolute -top-1 -right-1 bg-gold text-dark-primary text-xs px-1.5 py-0.5 rounded-full font-medium">
                            {t('cart.recommended')}
                          </div>
                        )}
                        <div className="font-medium text-sm">{size.name}</div>
                        <div className="text-xs mt-1">{size.dimensions}</div>
                        <div className="text-sm mt-1 font-light">€{size.price}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Alu-Dibond Prints */}
                <div>
                  <h4 className="text-sm text-gold mb-2">{t('cart.aluDibondPrints')}</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {PRINT_SIZES.filter(size => size.printType === 'aluDibond').map((size) => (
                      <button
                        key={size.id}
                        onClick={() => setSelectedSize(size)}
                        className={`p-2 rounded-lg border text-left transition-all ${
                          selectedSize.id === size.id
                            ? 'border-gold bg-gold/10 text-gold'
                            : 'border-dark-border/30 hover:border-gold/50 text-cream-dark hover:text-cream'
                        }`}
                      >
                        <div className="font-medium text-sm">{size.name}</div>
                        <div className="text-xs mt-1">{size.dimensions}</div>
                        <div className="text-sm mt-1 font-light">€{size.price}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Quantity Selection */}
              <div>
                <label className="block text-cream mb-3 font-light">{t('cart.quantity')}</label>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => handleQuantityChange(-1)}
                    className="w-10 h-10 rounded-full border border-dark-border/30 hover:border-gold/50 flex items-center justify-center text-cream hover:text-gold transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                    </svg>
                  </button>
                  <span className="text-2xl text-cream w-12 text-center">{quantity}</span>
                  <button
                    onClick={() => handleQuantityChange(1)}
                    className="w-10 h-10 rounded-full border border-dark-border/30 hover:border-gold/50 flex items-center justify-center text-cream hover:text-gold transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                </div>
              </div>

            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default AddToCartModal;